/**
 * Header do app delivery — paridade total com V1.
 *
 * Estrutura (mesma do V1):
 *   1. Hero / banner (se `store.banner_url` definido): vídeo ou foto com
 *      gradient + logo+nome em overlay.
 *      OU
 *      Barra vermelha (se sem banner): logo + nome + ações
 *      (busca/compartilhar).
 *   2. Barra de status SEMPRE renderizada abaixo: indicador Aberto/Fechado,
 *      mínimo de pedido, tempo de entrega.
 *
 * Ações (search/share/voltar-home) delegam pra handlers V1
 * (`openGlobalSearch`, `shareApp`, `goBackToHome`) — V1 continua dono.
 *
 * O botão de hambúrguer (abre `MenuDrawerIsland`) e a bandeirinha de idioma
 * moram aqui. O hambúrguer é `lg:hidden`: no computador a lista de categorias
 * já está na barra lateral fixa da home, então gaveta ali seria redundante.
 *
 * ONDE FICA O HAMBÚRGUER, E POR QUÊ. Na loja COM banner o herói rola pra fora
 * da tela e só a barra de status fica grudada no topo — o hambúrguer desenhado
 * sobre o banner sumia junto, e do meio do cardápio em diante não havia mais
 * como abrir o menu sem voltar ao topo (reclamação do dono, 15/09). Por isso
 * ele mora na BARRA DE STATUS, que é justamente a parte que gruda: existe no
 * topo e continua existindo depois de rolar, sempre no mesmo lugar. Na loja
 * SEM banner a barra vermelha já é sticky e leva o botão dela mesma — daí o
 * `comMenu` ser ligado só no caso do banner, pra não nascerem dois.
 */
import { useState } from 'react';
import { LanguagePicker } from './LanguagePicker';
import { useI18n } from '../i18n';

interface HeaderIslandProps {
  storeName: string;
  storeLogo: string | null;
  bannerUrl: string | null;
  bannerIsVideo: boolean;
  isOpen: boolean;
  deliveryEstimate: string;
  minOrderValue: number;
}

declare global {
  interface Window {
    openGlobalSearch?: () => void;
    shareApp?: () => void;
    goBackToHome?: () => void;
  }
}

/** Abre a gaveta de categorias. Some no computador (a lista já está fixa
 *  na lateral) e quando a loja tem cardápio de uma categoria só. */
function BotaoMenu({ sobreEscuro = false }: { sobreEscuro?: boolean }) {
  const { t } = useI18n();
  return (
    <button
      type="button"
      onClick={() => window.dispatchEvent(new Event('aliv:toggle-menu-drawer'))}
      aria-label={t('Abrir menu')}
      className={[
        'w-9 h-9 flex items-center justify-center rounded-full transition active:scale-95 shrink-0 lg:hidden',
        sobreEscuro ? 'bg-black/40 backdrop-blur-sm text-white hover:bg-black/60' : 'hover:bg-black/10',
      ].join(' ')}
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <i className="fas fa-bars text-base" />
    </button>
  );
}

function fmtBR(v: number): string {
  return v.toFixed(2).replace('.', ',');
}

function StatusBar({
  isOpen,
  deliveryEstimate,
  minOrderValue,
  comMenu = false,
}: {
  isOpen: boolean;
  deliveryEstimate: string;
  minOrderValue: number;
  comMenu?: boolean;
}) {
  const { t } = useI18n();
  return (
    <div className="bg-gray-50 pl-1 pr-4 py-2 flex items-center justify-between gap-2 text-xs font-medium text-gray-600 border-b border-gray-200 shadow-inner lg:hidden">
      <div className="flex items-center gap-1 min-w-0">
        {comMenu && <BotaoMenu />}
        <span className="flex items-center gap-2 min-w-0">
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span
              className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                isOpen ? 'bg-green-400' : 'bg-red-400'
              }`}
            />
            <span
              className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
                isOpen ? 'bg-green-500' : 'bg-red-500'
              }`}
            />
          </span>
          <span className={`font-bold truncate ${isOpen ? 'text-green-700' : 'text-red-600'}`}>
            {isOpen ? t('Loja Aberta') : t('Fechados no momento')}
          </span>
        </span>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {minOrderValue > 0 && (
          <span className="flex items-center gap-1 text-red-500 font-bold bg-red-50 px-2 py-0.5 rounded shadow-sm border border-red-100 text-[11px]">
            {t('Mín. {v}', { v: `R$ ${fmtBR(minOrderValue)}` })}
          </span>
        )}
        <span className="flex items-center gap-1 text-gray-500 font-bold bg-white px-2 py-0.5 rounded shadow-sm border border-gray-200">
          <i className="far fa-clock text-gray-400" /> {deliveryEstimate}
        </span>
      </div>
    </div>
  );
}

function HeroBanner({
  storeName,
  storeLogo,
  bannerUrl,
  bannerIsVideo,
  onBannerError,
}: {
  storeName: string;
  storeLogo: string | null;
  bannerUrl: string;
  bannerIsVideo: boolean;
  onBannerError: () => void;
}) {
  return (
    <div
      className="relative w-full bg-gray-900 overflow-hidden"
      style={{ aspectRatio: '21 / 9', maxHeight: 200 }}
    >
      {bannerIsVideo ? (
        <video
          src={bannerUrl}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="w-full h-full object-cover"
          poster={storeLogo || ''}
          onError={onBannerError}
        />
      ) : (
        <img
          src={bannerUrl}
          alt={storeName}
          className="w-full h-full object-cover"
          onError={onBannerError}
        />
      )}
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none" />

      <div className="absolute top-3 right-3 flex items-center gap-2">
        <LanguagePicker sobreEscuro />
        <button
          type="button"
          onClick={() => window.openGlobalSearch?.()}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition active:scale-95"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <i className="fas fa-search text-sm" />
        </button>
        <button
          type="button"
          onClick={() => window.shareApp?.()}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition active:scale-95"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <i className="fas fa-share-alt text-sm" />
        </button>
      </div>

      <div
        className="absolute bottom-3 left-4 right-4 flex items-end gap-3 cursor-pointer"
        onClick={() => window.goBackToHome?.()}
      >
        <div className="w-16 h-16 bg-white rounded-full p-1 shadow-lg ring-2 ring-white shrink-0">
          {storeLogo ? (
            <img
              src={storeLogo}
              alt={storeName}
              className="w-full h-full rounded-full object-contain"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src =
                  'https://placehold.co/100x100?text=Logo';
              }}
            />
          ) : (
            <div className="w-full h-full rounded-full bg-red-100 text-red-600 flex items-center justify-center">
              <i className="fas fa-utensils text-xl" />
            </div>
          )}
        </div>
        {/* 2 linhas em vez de cortar: lojas da mesma rede só se distinguem
            pelo FIM do nome ("Big Hall Pizzas - Fátima" x "… - Costa e
            Silva"). Com `truncate` as duas viravam "Big Hall Pizzas…" e o
            cliente não sabia em qual cardápio estava (pedido do lojista,
            18/09/2026). `min-w-0` deixa o flex encolher; sem ele o texto
            empurra o container e o clamp não vale. */}
        <h1 className="text-white text-xl font-black tracking-tight drop-shadow-lg pb-1 min-w-0 leading-tight line-clamp-2 break-words">
          {storeName}
        </h1>
      </div>
    </div>
  );
}

function RedHeaderBar({
  storeName,
  storeLogo,
}: {
  storeName: string;
  storeLogo: string | null;
}) {
  return (
    <div className="bg-red-600 text-white px-4 py-3 flex items-center justify-between gap-2">
      <BotaoMenu />
      <div
        className="flex items-center gap-3 cursor-pointer min-w-0 flex-1"
        onClick={() => window.goBackToHome?.()}
      >
        <div className="w-10 h-10 bg-white rounded-full p-0.5 shadow-sm shrink-0">
          {storeLogo ? (
            <img
              src={storeLogo}
              alt={storeName}
              className="w-full h-full rounded-full object-contain"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src =
                  'https://placehold.co/100x100?text=Logo';
              }}
            />
          ) : (
            <div className="w-full h-full rounded-full bg-red-100 text-red-600 flex items-center justify-center">
              <i className="fas fa-utensils text-sm" />
            </div>
          )}
        </div>
        {/* Sem `max-w-[180px]`: 180px cortava o nome ANTES do fim da barra,
            e era o que mais escondia a parte que distingue a unidade. O pai
            já é `min-w-0 flex-1`, então o título usa o espaço que sobra e
            quebra em 2 linhas quando precisa. */}
        <h1 className="text-lg font-black tracking-tight leading-tight line-clamp-2 break-words">
          {storeName}
        </h1>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <LanguagePicker />
        <button
          type="button"
          onClick={() => window.openGlobalSearch?.()}
          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-black/10 transition active:scale-95"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <i className="fas fa-search text-lg" />
        </button>
        <button
          type="button"
          onClick={() => window.shareApp?.()}
          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-black/10 transition active:scale-95"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <i className="fas fa-share-alt text-lg" />
        </button>
      </div>
    </div>
  );
}

export function HeaderIsland({
  storeName,
  storeLogo,
  bannerUrl,
  bannerIsVideo,
  isOpen,
  deliveryEstimate,
  minOrderValue,
}: HeaderIslandProps) {
  // assina a troca de idioma (ver i18n.ts)
  useI18n();
  // Banner que 404a (arquivo perdido em migração de VM — caso barbosa 26/08)
  // NÃO pode derrubar o header: antes o onError escondia o hero INTEIRO,
  // levando logo e nome junto. Agora erro de carga rebaixa pro layout
  // sem-banner (barra vermelha COM logo), igual a loja sem banner.
  const [bannerBroken, setBannerBroken] = useState(false);

  // Comportamento de scroll (paridade V1):
  //  - COM banner: hero NÃO é sticky (scrolla pra fora da viewport
  //    quando user rola). Só a barra de status fica grudada no topo.
  //    Pattern iFood/UberEats — quando rola, mostra menos chrome.
  //  - SEM banner: barra vermelha + status bar ficam JUNTAS sticky.
  if (bannerUrl && !bannerBroken) {
    return (
      <>
        <HeroBanner
          storeName={storeName}
          storeLogo={storeLogo}
          bannerUrl={bannerUrl}
          bannerIsVideo={bannerIsVideo}
          onBannerError={() => setBannerBroken(true)}
        />
        <div id="delivery-v2-header" className="sticky top-0 z-30 lg:static bg-white shadow-sm">
          <StatusBar
            comMenu
            isOpen={isOpen}
            deliveryEstimate={deliveryEstimate}
            minOrderValue={minOrderValue}
          />
        </div>
      </>
    );
  }

  return (
    <div id="delivery-v2-header" className="sticky top-0 z-30 lg:static bg-white shadow-sm">
      <RedHeaderBar storeName={storeName} storeLogo={storeLogo} />
      <StatusBar
        isOpen={isOpen}
        deliveryEstimate={deliveryEstimate}
        minOrderValue={minOrderValue}
      />
    </div>
  );
}
